import { useCallback, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { getDashboardStats } from "@/api/dashboard";
import { getUnassignedOrderCount } from "@/api/orders";
import { getDashboardReport } from "@/api/reports";
import { getRoutes } from "@/api/routes";
import { getFleetSummary, getLiveVehicles } from "@/api/tracking";
import { getVehicles } from "@/api/vehicles";
import { getPlanningSettings } from "@/api/settings";
import { RouteFormModal } from "@/features/routes/components/RouteFormModal";
import { ActionBand } from "@/features/dashboard/components/ActionBand";
import { MetricStrip } from "@/features/dashboard/components/MetricStrip";
import type { MetricCell } from "@/features/dashboard/components/MetricStrip";
import { ShiftTimeline } from "@/features/dashboard/components/ShiftTimeline";
import { TodayRoutesTable } from "@/features/dashboard/components/TodayRoutesTable";
import {
  buildActionCards,
  buildShiftSegments,
  parseDecimal,
  pickOfflineVehicles,
  sortByDeviation,
} from "@/features/dashboard/lib/panelData";
import {
  buildHourLabels,
  buildShiftWindow,
  nowTickIndex,
  tenantNowMinutes,
} from "@/features/dashboard/lib/shiftWindow";
import { formatSignalAge } from "@/features/tracking/lib/vehicleFilters";
import { useTenantTimezone } from "@/features/settings/hooks/useTenantTimezone";
import { useAuthStore } from "@/store/auth";
import { tenantTodayISO } from "@/lib/date";
import { cn } from "@/lib/cn";

/**
 * «Панель» — the dispatcher's work queue (TASK-5080, rebuilt to the owner's
 * mockup).
 *
 * What changed and why: the previous version was nine identical KPI tiles in a
 * flat grid, so «Не распределено 352» carried exactly the visual weight of
 * «Маршрутов в архиве 0» and nothing told a dispatcher what to DO. The panel is
 * now ordered by what demands action: a band of problem cards that each carry a
 * button, then a deliberately quieter metric strip, then the shift timeline,
 * then today's routes.
 *
 * Three rules run through the whole file and are easy to regress:
 *
 *  1. **A card at zero is not rendered.** Not greyed, not empty — absent. All
 *     four absent takes the heading and badge with it (`buildActionCards` +
 *     `ActionBand`).
 *  2. **`null` is not `0`.** A failed request omits its card and renders «—» in
 *     the strip; a measured zero renders «0». This is the existing
 *     `stops_undelivered` rule carried through every new figure.
 *  3. **Nothing is invented to fill the mockup.** A figure with no endpoint is
 *     left out with a TODO naming the field it needs. The mockup's 602 / 91 % /
 *     412 км are illustrative.
 */
export function DashboardTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // BUG-529: "today" in the TENANT's timezone, not a UTC slice of the browser
  // clock. The backend aggregates on `Route.date`, a plain DateField, so the
  // skew was entirely the frontend's choice of which day to ask for.
  const tenantTz = useTenantTimezone();
  const todayISO = tenantTodayISO(tenantTz);
  const queryClient = useQueryClient();
  const [isCreateRouteOpen, setIsCreateRouteOpen] = useState(false);
  const user = useAuthStore((state) => state.user);
  const tableRef = useRef<HTMLDivElement>(null);

  /**
   * TASK-5080 owner decision 1: «Посмотреть» stays on the panel — there is no
   * separate late-routes page. It sorts THIS table by deviation and scrolls to
   * it, and the sort is URL-addressable (`?sort=deviation`) precisely so the
   * button has something to target and the state survives a reload or a shared
   * link.
   */
  const sortByDeviationActive = searchParams.get("sort") === "deviation";

  const setDeviationSort = useCallback(
    (active: boolean) => {
      // Mutating a copy and writing once: react-router 7 does not compose two
      // updaters in one tick (the documented `useUrlFilters` gotcha).
      const next = new URLSearchParams(searchParams);
      if (active) next.set("sort", "deviation");
      else next.delete("sort");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const { data: routesData, isLoading: routesLoading } = useQuery({
    queryKey: ["routes-today", todayISO],
    queryFn: () => getRoutes({ date: todayISO }),
    // BUG-202: day-constant keys would otherwise serve the global 5-min cache
    // with no network refetch on tab-return.
    refetchOnMount: "always",
    staleTime: 0,
  });

  // BUG-527: every route-derived figure is a SERVER-SIDE aggregate, computed
  // before pagination — never summed from the loaded 20-row page.
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["dashboard-stats", todayISO],
    queryFn: () => getDashboardStats(todayISO),
    refetchOnMount: "always",
    staleTime: 0,
  });

  const {
    data: unassignedData,
    isLoading: unassignedLoading,
    isError: unassignedError,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ["unassigned-orders-count-dashboard"],
    queryFn: () => getUnassignedOrderCount({}),
    refetchOnMount: "always",
    staleTime: 0,
  });

  // «Без связи» reuses the EXISTING staleness definition rather than inventing
  // a second one: `fleet-summary` is the same count the tracking header shows,
  // and the live list supplies the vehicle to name in the detail line.
  const { data: fleetSummary, isError: fleetError } = useQuery({
    queryKey: ["fleet-summary", "dashboard"],
    queryFn: getFleetSummary,
    refetchOnMount: "always",
    staleTime: 0,
  });

  const { data: liveVehicles } = useQuery({
    queryKey: ["live-vehicles", "dashboard"],
    queryFn: getLiveVehicles,
    refetchOnMount: "always",
    staleTime: 0,
  });

  // On-time / utilisation for the strip. Reports endpoint over today's window.
  const { data: reportsKpis, isLoading: reportsKpisLoading } = useQuery({
    queryKey: ["reports", "dashboard", "today-kpis", todayISO],
    queryFn: () =>
      getDashboardReport({ date_from: todayISO, date_to: todayISO }),
    refetchOnMount: "always",
    staleTime: 0,
  });

  // The shift window's CLOSE has no tenant-level setting (there is no
  // `shift_end_time`), so it comes from the vehicles actually configured. See
  // `shiftWindow.ts`.
  const { data: planningSettings } = useQuery({
    queryKey: ["settings-planning"],
    queryFn: getPlanningSettings,
    staleTime: 5 * 60_000,
  });

  const { data: vehiclesData } = useQuery({
    queryKey: ["vehicles", "dashboard-shift-window"],
    queryFn: () => getVehicles({ page_size: 200 }),
    staleTime: 5 * 60_000,
  });

  /*
   * BUG-078 / TASK-5043: the VIEWER's clock, deliberately — this is "how long
   * ago YOUR screen last fetched", a property of this browser session, not
   * tenant data. Rendering it in the tenant's zone would print a time three
   * hours from now to a dispatcher in Warsaw.
   */
  const updatedAt = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : null;

  const routes = useMemo(() => routesData?.results ?? [], [routesData]);
  const statusCounts = stats?.status_counts;
  const sums = stats?.sums;

  // -------------------------------------------------------------------------
  // The action band
  // -------------------------------------------------------------------------

  const offlineVehicle = pickOfflineVehicles(liveVehicles, 1)[0];
  const offlineAge = formatSignalAge(offlineVehicle?.offline_for_sec);

  const cards = useMemo(() => {
    /**
     * Each source is `number | null`: `null` means NOT MEASURED (request
     * failed, still in flight, or the backend has no such field) and hides the
     * card for a different reason than a measured zero does — see
     * `ActionCardSources`.
     */
    return buildActionCards(
      {
        unassignedCount:
          unassignedLoading || unassignedError
            ? null
            : (unassignedData?.count ?? null),
        /**
         * TODO(backend): no route-level deviation exists on the routes list
         * today, so «Опаздывают» never renders. Needs `deviation_counts.late`
         * on `GET /v1/routes/` (computed before pagination; NOT a seventh
         * `status_counts` key — those six are disjoint and sum to the total,
         * while `late` is orthogonal). Passing `null` is what keeps the card
         * absent instead of asserting a confident zero.
         */
        lateCount: null,
        offlineCount: fleetError ? null : (fleetSummary?.offline ?? null),
        draftCount: statusCounts?.draft ?? null,
      },
      {
        /**
         * TODO(backend): the mockup's «128 из них с окном, закрывающимся
         * сегодня» needs `closing_today` on
         * `GET /v1/orders/unassigned-count/`. Omitted rather than faked.
         */
        unassigned: null,
        late: null,
        offline:
          offlineVehicle && offlineAge
            ? t("dashboard.actionBand.offline.detail", {
                vehicle: offlineVehicle.vehicle_name,
                // DYNAMIC i18n KEY (`liveTracking.panel.signalUnit.${unit}`).
                // The four keys (sec/min/hour/day) already exist and are the
                // tracking table's own — REUSED, not re-spelled, so the panel
                // and the tracker cannot word the same age differently.
                // Invisible to `check:i18n`; pinned by a rendered-text test.
                age: `${offlineAge.value} ${t(`liveTracking.panel.signalUnit.${offlineAge.unit}`)}`,
              })
            : null,
        drafts: t("dashboard.actionBand.drafts.detail"),
      },
    );
  }, [
    unassignedLoading,
    unassignedError,
    unassignedData,
    fleetError,
    fleetSummary,
    statusCounts,
    offlineVehicle,
    offlineAge,
    t,
  ]);

  const handleViewLate = useCallback(() => {
    setDeviationSort(true);
    tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [setDeviationSort]);

  // -------------------------------------------------------------------------
  // The metric strip
  // -------------------------------------------------------------------------

  const totalRoutes = stats?.total_routes ?? null;
  const totalStops = sums?.stops ?? null;
  const totalDistanceKm = parseDecimal(sums?.distance);
  const undelivered = stats?.stops_undelivered ?? null;
  /*
    TASK-5082 landed `sums.delivered` / `sums.delivered_late`, so the «Точки»
    fraction and the shift timeline are sourced rather than withheld.

    `?? null` and NOT `?? 0`: an old server omits the key entirely, and a zero
    there would claim "nothing delivered today" — a measurement nobody took.
    Null withholds the fraction and the timeline instead, which is the same
    honesty rule `stops_undelivered` already follows.
  */
  const delivered = sums?.delivered ?? null;
  const deliveredLate = sums?.delivered_late ?? null;

  const metricCells = useMemo<MetricCell[]>(() => {
    const planned = (statusCounts?.draft ?? 0) + (statusCounts?.sent ?? 0);
    const active = statusCounts?.active ?? 0;
    const completed = statusCounts?.completed ?? 0;

    const onTime = reportsKpis?.otd_percent;
    const utilisation = reportsKpis?.avg_utilization_weight_pct;

    const perStopKm =
      totalDistanceKm != null && totalStops != null && totalStops > 0
        ? (totalDistanceKm / totalStops).toFixed(1)
        : null;

    return [
      {
        id: "routes",
        label: t("dashboard.strip.routes"),
        value: statsLoading || totalRoutes == null ? null : String(totalRoutes),
        sub: statusCounts
          ? t("dashboard.strip.routesSub", { active, completed, planned })
          : null,
      },
      {
        id: "stops",
        label: t("dashboard.strip.stops"),
        /*
          «86 из 198» — the delivered count over the day's total (TASK-5082).
          When `delivered` is absent (an older server) the fraction degrades to
          the bare total rather than inventing a numerator.
        */
        value: statsLoading
          ? null
          : delivered != null
            ? String(delivered)
            : totalStops != null
              ? String(totalStops)
              : null,
        valueSuffix:
          delivered != null && totalStops != null
            ? t("dashboard.strip.stopsOf", { total: totalStops })
            : null,
        sub: null,
      },
      {
        id: "onTime",
        label: t("dashboard.strip.onTime"),
        value: reportsKpisLoading || onTime == null ? null : `${onTime}%`,
        sub: null,
      },
      {
        id: "undelivered",
        label: t("dashboard.strip.undelivered"),
        // `?? null`, never `?? 0`: a missing aggregate must not read as a
        // confident "nothing failed".
        value: statsLoading || undelivered == null ? null : String(undelivered),
        // Plural set (BUG-719): «1 точка», «3 точки», «5 точек».
        valueSuffix:
          undelivered == null
            ? null
            : t("dashboard.strip.undeliveredUnit", { count: undelivered }),
        emphasis: undelivered != null && undelivered > 0 ? "danger" : null,
        sub: null,
      },
      {
        id: "utilisation",
        label: t("dashboard.strip.utilisation"),
        value:
          reportsKpisLoading || utilisation == null ? null : `${utilisation}%`,
        sub: t("dashboard.strip.utilisationSub"),
      },
      {
        id: "mileage",
        label: t("dashboard.strip.mileage"),
        value:
          statsLoading || totalDistanceKm == null
            ? null
            : String(Math.round(totalDistanceKm)),
        valueSuffix: t("units.km"),
        sub: perStopKm
          ? t("dashboard.strip.mileageSub", { perStop: perStopKm })
          : null,
      },
    ];
  }, [
    statsLoading,
    statusCounts,
    totalRoutes,
    totalStops,
    totalDistanceKm,
    delivered,
    undelivered,
    reportsKpis,
    reportsKpisLoading,
    t,
  ]);

  // -------------------------------------------------------------------------
  // «ХОД СМЕНЫ»
  // -------------------------------------------------------------------------

  const shiftWindow = useMemo(
    () =>
      buildShiftWindow(
        planningSettings?.shift_start_time,
        (vehiclesData?.results ?? []).map((v) => v.shift_end),
      ),
    [planningSettings, vehiclesData],
  );

  const shift = useMemo(
    () =>
      buildShiftSegments({
        totalStops,
        /*
          Both sourced since TASK-5082. `deliveredLate` is a SUBSET of
          `delivered`, so `buildShiftSegments` derives the on-time segment as
          `delivered - deliveredLate` rather than treating them as siblings.

          Still `?? null` at the read above, never `?? 0`: an absent key means
          an old server, and a zero would draw a full-width «осталось» bar
          asserting that nothing was delivered today.
        */
        delivered,
        deliveredLate,
        undelivered,
      }),
    [totalStops, delivered, deliveredLate, undelivered],
  );

  const hourLabels = useMemo(
    () => (shiftWindow ? buildHourLabels(shiftWindow) : []),
    [shiftWindow],
  );

  const nowMinutes = tenantNowMinutes(tenantTz);
  const nowIndex = shiftWindow
    ? nowTickIndex(shiftWindow, nowMinutes, hourLabels.length)
    : null;

  // -------------------------------------------------------------------------
  // Today's routes
  // -------------------------------------------------------------------------

  const visibleRoutes = useMemo(
    () => (sortByDeviationActive ? sortByDeviation(routes) : routes),
    [routes, sortByDeviationActive],
  );

  const dispatcherName = user
    ? `${user.first_name} ${user.last_name}`.trim() || user.email
    : null;

  const headerBits = [
    shiftWindow ? `${shiftWindow.startLabel}—${shiftWindow.endLabel}` : null,
    updatedAt ? t("dashboard.updatedAt", { time: updatedAt }) : null,
    dispatcherName
      ? t("dashboard.dispatcherName", { name: dispatcherName })
      : null,
  ].filter((bit): bit is string => bit != null);

  return (
    <div className="space-y-4">
      {/* Header line — shift window · last updated · dispatcher, plus the one
          primary action. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-gray-400">{headerBits.join(" · ")}</p>
        <button
          type="button"
          onClick={() => setIsCreateRouteOpen(true)}
          className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-lg bg-brand-primary px-3.5 text-sm font-semibold text-white transition-colors hover:bg-brand-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t("dashboard.newRoute")}
        </button>
      </div>

      {/* «ТРЕБУЕТ РЕШЕНИЯ». Renders nothing at all while loading or when every
          count is zero — no skeleton, which would flash four phantom problems
          before resolving to none. */}
      <ActionBand
        cards={cards}
        units={{
          // Plural sets (BUG-719): the unit agrees with the card's own count —
          // «1 машина», «4 маршрута», «5 маршрутов».
          unassigned: (count) =>
            t("dashboard.actionBand.unassigned.unit", { count }),
          late: (count) => t("dashboard.actionBand.late.unit", { count }),
          offline: (count) => t("dashboard.actionBand.offline.unit", { count }),
          drafts: (count) => t("dashboard.actionBand.drafts.unit", { count }),
        }}
        handlers={{
          // The unassigned POOL is not URL-addressable on /orders (an order can
          // be `new` and already sit on a draft route), so this lands on the
          // Routes page, which owns the unassigned slide-over and the build
          // flow — the place the work actually gets done.
          onUnassigned: () => navigate("/routes"),
          onLate: handleViewLate,
          onOffline: () => navigate("/tracking"),
          onDrafts: () =>
            navigate(
              `/routes?status=draft&date_from=${todayISO}&date_to=${todayISO}`,
            ),
        }}
      />

      <MetricStrip cells={metricCells} />

      {/* Rendered only when both the window and the segments are measurable;
          an un-drawable timeline is omitted, never faked. */}
      {shiftWindow && shift && (
        <ShiftTimeline
          total={shift.total}
          segments={shift.segments}
          hourLabels={hourLabels}
          nowIndex={nowIndex}
          caption={null}
        />
      )}

      {/* Today's routes */}
      <div ref={tableRef}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900">
              {t("dashboard.todaysRoutes")}
            </h3>
            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-500">
              {statsLoading ? "…" : (stats?.total_routes ?? routes.length)}
            </span>
            <button
              type="button"
              aria-pressed={sortByDeviationActive}
              onClick={() => setDeviationSort(!sortByDeviationActive)}
              className={cn(
                "cursor-pointer rounded-full px-3 py-1 text-xs font-semibold transition-colors",
                // SELECTED state is a runtime BRAND token, never a fixed dark
                // neutral (`bg-gray-900` as "chosen" is the Sprint 184 defect).
                sortByDeviationActive
                  ? "bg-brand-sidebar text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200",
              )}
            >
              {t("dashboard.sortByDeviation")}
            </button>
          </div>
          <Link
            to="/routes"
            className="text-sm font-semibold text-brand-primary hover:underline"
          >
            {t("dashboard.allRoutes")}
          </Link>
        </div>
        <TodayRoutesTable
          routes={visibleRoutes}
          isLoading={routesLoading}
          onCreateRoute={() => setIsCreateRouteOpen(true)}
        />
      </div>

      <RouteFormModal
        mode="create"
        open={isCreateRouteOpen}
        onOpenChange={setIsCreateRouteOpen}
        onSuccess={() => {
          void queryClient.invalidateQueries({
            queryKey: ["routes-today", todayISO],
          });
          void queryClient.invalidateQueries({
            queryKey: ["dashboard-stats", todayISO],
          });
        }}
      />
    </div>
  );
}
